import React, { useState, Children } from 'react';
import { individualsByPeriod, teamsByPeriod, TimePeriod } from '../data/mockData';
import { LeaderboardRow } from './LeaderboardRow';
import { Podium } from './Podium';
import { Users, User, Calendar, CalendarDays, CalendarRange, FileText, Send, Award, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const periods: {
  key: TimePeriod;
  label: string;
  icon: React.ElementType;
}[] = [{
  key: 'daily',
  label: 'Today',
  icon: Calendar
}, {
  key: 'weekly',
  label: 'This Week',
  icon: CalendarDays
}, {
  key: 'monthly',
  label: 'This Month',
  icon: CalendarRange
}];
const statsContainerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
const statsItemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
const listContainerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.08
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.12
    }
  }
};
const listItemVariants = {
  hidden: {
    opacity: 0,
    y: 8
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 26
    }
  }
};
const statCards = [{
  key: 'docs',
  label: 'Total Docs',
  icon: FileText
}, {
  key: 'subs',
  label: 'Total Subs',
  icon: Send
}, {
  key: 'ctcs',
  label: 'Total CTCs',
  icon: Award
}, {
  key: 'avg',
  label: 'Avg Score',
  icon: TrendingUp
}] as const;
export function Leaderboard() {
  const [view, setView] = useState<'individual' | 'team'>('individual');
  const [period, setPeriod] = useState<TimePeriod>('monthly');
  const data = view === 'individual' ? individualsByPeriod[period] : teamsByPeriod[period];
  const maxScore = Math.max(...data.map(d => d.score));
  const totalDocOuts = data.reduce((sum, d) => sum + d.docOuts, 0);
  const totalSubmissions = data.reduce((sum, d) => sum + d.submissions, 0);
  const totalCTCs = data.reduce((sum, d) => sum + d.ctcs, 0);
  const averageScore = Math.round(data.reduce((sum, d) => sum + d.score, 0) / (data.length || 1));
  const statValues = {
    docs: totalDocOuts,
    subs: totalSubmissions,
    ctcs: totalCTCs,
    avg: averageScore
  };
  const filterKey = `${view}-${period}`;
  return <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8" data-id="element-11">
      {/* Controls */}
      <div className="flex flex-col gap-4 mb-6" data-id="element-12">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3" data-id="element-13">
          <h2 className="text-xl sm:text-2xl font-bold text-ez-dark" data-id="element-14">
            Current Standings
          </h2>
          <div className="bg-gray-100 p-1 rounded-lg flex shadow-inner" data-id="element-15">
            <button onClick={() => setView('individual')} className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 ${view === 'individual' ? 'bg-white text-ez-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-16">
              <User className="w-4 h-4" data-id="element-17" />
              Individual
            </button>
            <button onClick={() => setView('team')} className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 ${view === 'team' ? 'bg-white text-ez-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-18">
              <Users className="w-4 h-4" data-id="element-19" />
              Team
            </button>
          </div>
        </div>

        <div className="flex justify-center sm:justify-start gap-2" data-id="element-20">
          {periods.map(({
          key,
          label,
          icon: Icon
        }) => <button key={key} onClick={() => setPeriod(key)} className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 border ${period === key ? 'bg-ez-green text-white border-ez-green shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:border-ez-green/40 hover:text-gray-700'}`} data-id="element-21">
              <Icon className="w-4 h-4" data-id="element-22" />
              {label}
            </button>)}
        </div>
      </div>

      {/* Summary Stats */}
      <motion.div variants={statsContainerVariants} initial="hidden" animate="show" key={filterKey} className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-id="element-23">
        {statCards.map(({
        key,
        label,
        icon: StatIcon
      }) => <motion.div key={key} variants={statsItemVariants} className="bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-ez-green p-3.5 flex items-center justify-between" data-id="element-24">
            <div data-id="element-25">
              <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-26">
                {label}
              </p>
              <p className="text-xl sm:text-2xl font-black text-gray-900 leading-none tabular-nums" data-id="element-27">
                {statValues[key].toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-50 p-2 rounded-lg text-gray-300" data-id="element-28">
              <StatIcon className="w-5 h-5" data-id="element-29" />
            </div>
          </motion.div>)}
      </motion.div>

      {/* Podium */}
      <Podium entries={data} data-id="element-30" />

      {/* Column Headers */}
      <div className="hidden sm:flex items-center px-4 py-2 mb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-31">
        <div className="w-8 mr-3 text-center" data-id="element-32">Rank</div>
        <div className="flex-1" data-id="element-33">Name</div>
        <div className="w-32 xl:w-44 hidden md:block" data-id="element-34">Badges</div>
        <div className="flex gap-6 lg:gap-8" data-id="element-35">
          <div className="w-10 text-right" data-id="element-36">Docs</div>
          <div className="w-10 text-right" data-id="element-37">Subs</div>
          <div className="w-10 text-right" data-id="element-38">CTCs</div>
        </div>
        <div className="w-20 lg:w-24 text-right" data-id="element-39">Score</div>
      </div>

      {/* List */}
      <AnimatePresence mode="wait" data-id="element-40">
        <motion.div key={filterKey} variants={listContainerVariants} initial="hidden" animate="show" exit="exit" className="flex flex-col gap-2" data-id="element-41">
          {data.slice(3).map((entry, index) => <motion.div key={entry.id} variants={listItemVariants} data-id="element-42">
              <LeaderboardRow entry={entry} rank={index + 4} maxScore={maxScore} data-id="element-43" />
            </motion.div>)}
        </motion.div>
      </AnimatePresence>
    </div>;
}